import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, ArrowRight, CreditCard, Stethoscope, Truck, Usb, Package, Gift, ShoppingCart } from 'lucide-react';
import { useCurrency, CurrencySelector } from '../components/CurrencySelector';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatPrice, currency } = useCurrency();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activePlanType, setActivePlanType] = useState('diagnose');
  const [promoCode, setPromoCode] = useState('');
  const [promoDetails, setPromoDetails] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [availableCredits, setAvailableCredits] = useState(0);
  const [applyCredits, setApplyCredits] = useState(false);
  // Early Bird State
  const [earlyBirdInfo, setEarlyBirdInfo] = useState(null);
  // Dongle Upsell State
  const [dongleProducts, setDongleProducts] = useState([]);
  const [selectedDongle, setSelectedDongle] = useState(null);
  const [addDongle, setAddDongle] = useState(false);
  const [dongleFreeWithPromo, setDongleFreeWithPromo] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company: '',
    phone: '',
    street: '',
    city: '',
    postal_code: '',
    country: 'Deutschland',
    subscribeNewsletter: true
  });

  // Fetch Early Bird status on mount
  useEffect(() => {
    const fetchEarlyBirdStatus = async () => {
      try {
        const response = await axios.get(`${API}/launch/status`);
        if (response.data.is_before_launch && response.data.current_tier) {
          setEarlyBirdInfo(response.data);
        }
      } catch (error) {
        console.log('Early bird status not available');
      }
    };
    fetchEarlyBirdStatus();
  }, []);

  useEffect(() => {
    checkPaymentStatus();
    fetchUserCredits();
    fetchDongleProducts();
    
    // Check for referral code from URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Track click
      trackReferralClick(refCode);
    }
    
    // Check for promo code from popup or URL
    const storedPromo = sessionStorage.getItem('promo_code');
    const storedDetails = sessionStorage.getItem('promo_details');
    if (storedPromo) {
      setPromoCode(storedPromo);
    }
    if (storedDetails) {
      try {
        const details = JSON.parse(storedDetails);
        setPromoDetails(details);
        // Check if promo includes free dongle
        if (details.free_dongle) {
          setDongleFreeWithPromo(true);
          setAddDongle(true);
        }
      } catch (e) {
        console.error('Failed to parse promo details:', e);
      }
    }
    
    // Check for dongle parameter from URL
    const dongleParam = searchParams.get('dongle');
    if (dongleParam === 'true' || dongleParam) {
      setAddDongle(true);
    }
  }, [searchParams]);

  const fetchDongleProducts = async () => {
    try {
      const response = await axios.get(`${API}/dongle-shop/products`);
      const products = response.data.products || [];
      setDongleProducts(products);
      
      // Select featured product or first one as default
      const featured = products.find(p => p.is_featured) || products[0];
      if (featured) {
        setSelectedDongle(featured);
      }
      
      // Check URL for specific dongle ID
      const dongleParam = searchParams.get('dongle');
      if (dongleParam && dongleParam !== 'true') {
        const specificDongle = products.find(p => p.id === dongleParam);
        if (specificDongle) {
          setSelectedDongle(specificDongle);
          setAddDongle(true);
        }
      }
    } catch (error) {
      console.error('Failed to load dongle products:', error);
    }
  };

  const fetchUserCredits = async () => {
    const token = localStorage.getItem('token');
    if (!token) return; // No logged-in user
    
    try {
      const response = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const credits = response.data.available_credits || 0;
      setAvailableCredits(credits);
      console.log(`User has ${credits}€ in credits`);
    } catch (error) {
      // User not logged in or credits not available
      console.log('No credits available (user not logged in)');
    }
  };

  const trackReferralClick = async (code) => {
    try {
      await axios.post(`${API}/referral/track-click/${code}`);
    } catch (error) {
      console.error('Failed to track referral click:', error);
    }
  };

  // Separate effect for fetching plans when activePlanType or promoDetails changes
  useEffect(() => {
    fetchPlans(activePlanType);
  }, [activePlanType, promoDetails]);

  const fetchPlans = async (planType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/subscriptions/plans?planType=${planType}`);
      let fetchedPlans = response.data || [];
      
      // Filter plans if promo has eligible_plan_ids specified
      if (promoDetails && promoDetails.eligible_plan_ids && promoDetails.eligible_plan_ids.length > 0) {
        // Only show plans that are in the eligible list
        fetchedPlans = fetchedPlans.filter(plan => 
          promoDetails.eligible_plan_ids.includes(plan.id)
        );
        console.log('Filtered to eligible plans:', fetchedPlans.map(p => p.name));
      }
      
      // Filter by billing interval if requires_12_month is set
      if (promoDetails && (promoDetails.requires_12_month_plan || promoDetails.requires_12_month)) {
        // Only show monthly plans (exclude one-time/lifetime plans)
        fetchedPlans = fetchedPlans.filter(plan => {
          const interval = plan.billing_interval || plan.interval;
          return interval === 'monthly' || interval === 'month';
        });
      }
      
      setPlans(fetchedPlans);
      // Reset selected plan when switching tabs
      setSelectedPlan(null);
    } catch (error) {
      toast.error('Fehler beim Laden der Pläne');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10; // Increased from 5 to 10 (20 seconds total)
    
    if (attempts >= maxAttempts) {
      toast.error('Zahlungsstatus-Prüfung abgelaufen. Bitte kontaktieren Sie den Support.');
      return;
    }

    try {
      console.log(`[Payment Status] Checking attempt ${attempts + 1}/${maxAttempts} for session ${sessionId}`);
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      console.log('[Payment Status] Response:', response.data);
      
      // Check if payment is successful and user is provisioned
      if (response.data.payment_status === 'paid' && response.data.user_provisioned) {
        toast.success('🎉 Zahlung erfolgreich! Ihr Account wurde erstellt. Sie werden zum Login weitergeleitet...');
        setTimeout(() => navigate('/auth'), 3000);
        return;
      } else if (response.data.payment_status === 'paid' && !response.data.user_provisioned) {
        // Payment successful but user not yet provisioned - continue polling
        console.log('[Payment Status] Payment successful, waiting for user provisioning...');
        toast.info('Zahlung erfolgreich! Ihr Account wird gerade erstellt...');
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Zahlungssitzung abgelaufen. Bitte versuchen Sie es erneut.');
        return;
      } else {
        // Payment not yet complete - continue polling
        console.log(`[Payment Status] Payment status: ${response.data.payment_status}, retrying...`);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('[Payment Status] Error checking payment status:', error);
      // Continue polling even on error (might be temporary network issue)
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        toast.error('Fehler bei der Statusprüfung. Bitte kontaktieren Sie den Support mit Ihrer Session ID: ' + sessionId.substring(0, 20) + '...');
      }
    }
  };

  // Calculate final price (considering 12-month requirement)
  const calculateFinalPrice = (plan) => {
    const interval = plan.billing_interval || plan.interval;
    const basePrice = plan.price;
    
    // If promo requires 12-month plan AND this is a monthly plan, calculate annual price
    if (promoDetails && promoDetails.requires_12_month_plan && (interval === 'monthly' || interval === 'month')) {
      return basePrice * 12;
    }
    
    return basePrice;
  };

  // Calculate discounted price based on promo AND early bird
  const calculateDiscountedPrice = (originalPrice) => {
    let discountedPrice = originalPrice;
    
    // Apply Early Bird discount first (if active and no promo code)
    if (earlyBirdInfo && earlyBirdInfo.current_tier && !promoDetails) {
      const earlyBirdDiscount = earlyBirdInfo.current_tier.discount_percent || 0;
      discountedPrice = originalPrice * (1 - earlyBirdDiscount / 100);
    }
    
    // Apply promo code discount (overrides early bird if present)
    if (promoDetails && promoDetails.is_active) {
      if (promoDetails.discount_type === 'percentage') {
        discountedPrice = originalPrice * (1 - promoDetails.discount_value / 100);
      } else if (promoDetails.discount_type === 'fixed') {
        discountedPrice = Math.max(0, originalPrice - promoDetails.discount_value);
      }
    }
    
    return Math.round(discountedPrice * 100) / 100;
  };

  // Get current discount info for display
  const getCurrentDiscountInfo = () => {
    if (promoDetails && promoDetails.is_active) {
      return {
        type: 'promo',
        label: promoCode,
        percent: promoDetails.discount_type === 'percentage' ? promoDetails.discount_value : null,
        isLifetime: false
      };
    }
    if (earlyBirdInfo && earlyBirdInfo.current_tier) {
      return {
        type: 'early_bird',
        label: `${earlyBirdInfo.current_tier.name} ${earlyBirdInfo.current_tier.badge || '🐦'}`,
        percent: earlyBirdInfo.current_tier.discount_percent,
        isLifetime: true,
        daysLeft: earlyBirdInfo.days_until_launch
      };
    }
    return null;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!selectedPlan) {
      toast.error('Bitte wählen Sie einen Plan aus');
      return false;
    }

    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return false;
    }

    // Trim passwords for comparison
    const password = formData.password.trim();
    const confirmPassword = formData.confirmPassword.trim();
    
    if (password !== confirmPassword) {
      console.log('Password mismatch:', { password: password.length, confirm: confirmPassword.length });
      toast.error('Passwörter stimmen nicht überein. Bitte überprüfen Sie Ihre Eingabe.');
      return false;
    }

    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return false;
    }

    return true;
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;

    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      
      // Build dongle upsell data
      let dongleUpsell = null;
      if (addDongle && selectedDongle) {
        dongleUpsell = {
          product_id: selectedDongle.id,
          product_name: selectedDongle.name,
          price: dongleFreeWithPromo ? 0 : selectedDongle.price,
          is_free: dongleFreeWithPromo
        };
      }
      
      const response = await axios.post(`${API}/checkout/create-session`, {
        plan_id: selectedPlan.id,
        user_data: formData,
        origin_url: originUrl,
        promo_code: promoCode || null,
        promo_details: promoDetails || null,
        referral_code: referralCode || null,
        apply_credits: applyCredits && availableCredits > 0 ? availableCredits : null,
        dongle_upsell: dongleUpsell
      });

      // Handle FREE plans (direct account creation)
      if (response.data.free_account) {
        toast.success('Account erfolgreich erstellt! Sie können sich jetzt anmelden.');
        setTimeout(() => navigate('/auth'), 2000);
        return;
      }

      // Handle PAID plans (Stripe checkout)
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error('Zahlungssystem ist noch nicht konfiguriert. Bitte kontaktieren Sie den Support.');
      } else if (error.response?.status === 400 && error.response?.data?.detail?.includes('bereits registriert')) {
        toast.error('Diese E-Mail ist bereits registriert. Bitte melden Sie sich an.');
      } else {
        toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Checkout-Session');
      }
      console.error(error);
      setProcessing(false);
    }
  };
  
  // Calculate total price including dongle
  const calculateTotalPrice = () => {
    let total = 0;
    
    if (selectedPlan) {
      const planPrice = calculateDiscountedPrice(calculateFinalPrice(selectedPlan));
      total += planPrice;
    }
    
    if (addDongle && selectedDongle && !dongleFreeWithPromo) {
      total += selectedDongle.price;
    }
    
    return total;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <p style={{ color: '#94A3B8' }}>Laden...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-6 flex justify-between items-center">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-gray-400 hover:text-white"
          >
            ← Zurück zur Startseite
          </Button>
          <div className="flex items-center gap-2">
            <span style={{ color: '#94A3B8', fontSize: '14px' }}>Währung:</span>
            <CurrencySelector />
          </div>
        </div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Account erstellen
          </h1>
          <p className="text-xl" style={{ color: '#94A3B8' }}>
            Wählen Sie Ihren Service und Plan - starten Sie in wenigen Minuten
          </p>
        </div>

        {/* Early Bird Banner */}
        {earlyBirdInfo && earlyBirdInfo.current_tier && (
          <div 
            className="mb-10 p-6 rounded-xl relative overflow-hidden"
            style={{ 
              background: 'linear-gradient(135deg, #F59E0B20 0%, #FCD34D10 50%, #F59E0B20 100%)',
              border: '2px solid #F59E0B'
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-5xl">{earlyBirdInfo.current_tier.badge || '🐦'}</div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-2xl font-bold" style={{ color: '#F59E0B' }}>
                      {earlyBirdInfo.current_tier.name}
                    </h2>
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-bold animate-pulse"
                      style={{ backgroundColor: '#F59E0B', color: '#0B0F14' }}
                    >
                      LEBENSLANGER RABATT
                    </span>
                  </div>
                  <p className="mt-1" style={{ color: '#FCD34D' }}>
                    Sichern Sie sich jetzt <strong>{earlyBirdInfo.current_tier.discount_percent}% Rabatt</strong> auf alle Pläne - für immer!
                  </p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <div className="text-4xl font-black" style={{ color: '#F59E0B' }}>
                  -{earlyBirdInfo.current_tier.discount_percent}%
                </div>
                <div className="flex items-center gap-2 mt-1" style={{ color: '#94A3B8' }}>
                  <span>⏰</span>
                  <span>Noch <strong style={{ color: '#FCD34D' }}>{earlyBirdInfo.days_until_launch} Tage</strong> bis Launch</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plan Type Tabs */}
        <div className="mb-12">
          <Tabs value={activePlanType} onValueChange={(value) => setActivePlanType(value)}>
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2" style={{ backgroundColor: '#1F2937' }}>
              <TabsTrigger 
                value="diagnose" 
                className="flex items-center gap-2"
                style={{ 
                  color: activePlanType === 'diagnose' ? '#fff' : '#94A3B8',
                  backgroundColor: activePlanType === 'diagnose' ? '#1EA0E6' : 'transparent'
                }}
              >
                <Stethoscope className="w-4 h-4" />
                Diagnose
              </TabsTrigger>
              <TabsTrigger 
                value="fms"
                className="flex items-center gap-2"
                style={{ 
                  color: activePlanType === 'fms' ? '#fff' : '#94A3B8',
                  backgroundColor: activePlanType === 'fms' ? '#1EA0E6' : 'transparent'
                }}
              >
                <Truck className="w-4 h-4" />
                Flottenmanagement
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Service Description */}
          <div className="max-w-2xl mx-auto mt-6 p-4 rounded-lg" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', border: '1px solid' }}>
            {activePlanType === 'diagnose' ? (
              <p className="text-center" style={{ color: '#94A3B8' }}>
                🔍 <strong style={{ color: '#7CD4FD' }}>KI-gestützte Fahrzeugdiagnose</strong> - Fehlercodes verstehen, Reparaturanleitungen erhalten und bis zu 87% Kosten sparen
              </p>
            ) : (
              <p className="text-center" style={{ color: '#94A3B8' }}>
                🚚 <strong style={{ color: '#7CD4FD' }}>Professionelles Flottenmanagement</strong> - GPS-Tracking, OBD-Diagnose, Wartungsplanung und Fahrzeugpool-Verwaltung
              </p>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Plan Selection */}
          <div className="lg:col-span-3 mb-8">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#E6EDF3' }}>
              1. Wählen Sie Ihren Plan
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan, idx) => (
                <Card
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className="cursor-pointer transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: '#0F141A',
                    borderColor: selectedPlan?.id === plan.id ? '#1EA0E6' : '#1F2937',
                    borderWidth: selectedPlan?.id === plan.id ? '2px' : '1px'
                  }}
                >
                  {idx === 1 && (
                    <div
                      className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      BELIEBT
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle style={{ color: '#E6EDF3' }}>{plan.name}</CardTitle>
                    <div className="mt-4">
                      {promoDetails && promoDetails.is_active ? (
                        <>
                          <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold line-through" style={{ color: '#94A3B8' }}>
                              {formatPrice(calculateFinalPrice(plan))}
                            </span>
                            <span className="text-4xl font-bold" style={{ color: '#22C55E' }}>
                              {formatPrice(calculateDiscountedPrice(calculateFinalPrice(plan)))}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                              {promoDetails.discount_type === 'percentage' ? `${promoDetails.discount_value}% RABATT` : `${formatPrice(promoDetails.discount_value)} RABATT`}
                            </span>
                            <span style={{ color: '#94A3B8' }}>
                              {(promoDetails.requires_12_month_plan || promoDetails.requires_12_month) && (plan.billing_interval === 'monthly' || plan.interval === 'month') 
                                ? '/12 Monate' 
                                : (plan.billing_interval === 'monthly' || plan.interval === 'month') ? '/Monat' 
                                : (plan.billing_interval === 'yearly' || plan.interval === 'year') ? '/Jahr' 
                                : ' einmalig'}
                            </span>
                          </div>
                          {promoDetails.first_month_free && (plan.billing_interval === 'monthly' || plan.interval === 'month') && !(promoDetails.requires_12_month_plan || promoDetails.requires_12_month) && (
                            <span className="inline-block mt-2 px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#7CD4FD20', color: '#7CD4FD' }}>
                              + Erster Monat gratis!
                            </span>
                          )}
                          {(promoDetails.requires_12_month_plan || promoDetails.requires_12_month) && (plan.billing_interval === 'monthly' || plan.interval === 'month') && (
                            <span className="inline-block mt-2 px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                              ⚠️ Zahlung für 12 Monate
                            </span>
                          )}
                        </>
                      ) : earlyBirdInfo && earlyBirdInfo.current_tier ? (
                        <>
                          {/* Early Bird Price Display */}
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold line-through" style={{ color: '#94A3B8' }}>
                              {formatPrice(plan.price)}
                            </span>
                            <span className="text-4xl font-bold" style={{ color: '#F59E0B' }}>
                              {formatPrice(calculateDiscountedPrice(plan.price))}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="px-2 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                              {earlyBirdInfo.current_tier.badge} {earlyBirdInfo.current_tier.discount_percent}% LEBENSLANG
                            </span>
                            <span style={{ color: '#94A3B8' }}>
                              {(plan.billing_interval === 'monthly' || plan.interval === 'month') ? '/Monat' : 
                               (plan.billing_interval === 'yearly' || plan.interval === 'year') ? '/Jahr' : ' einmalig'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold" style={{ color: '#7CD4FD' }}>
                            {formatPrice(plan.price)}
                          </span>
                          <span style={{ color: '#94A3B8' }}>
                            {(plan.billing_interval === 'monthly' || plan.interval === 'month') ? '/Monat' : 
                             (plan.billing_interval === 'yearly' || plan.interval === 'year') ? '/Jahr' : ' einmalig'}
                          </span>
                        </>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {/* Diagnose Plan Features */}
                      {activePlanType === 'diagnose' && (
                        <>
                          <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                            <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                            {plan.limits?.sessions_per_month || 'Unbegrenzte'} Diagnosen/Monat
                          </li>
                          {plan.features?.pdf_reports && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              PDF Reports
                            </li>
                          )}
                          {plan.features?.dual_ai_system && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Dual-AI System
                            </li>
                          )}
                          {plan.features?.advanced_diagnosis && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Erweiterte Diagnose
                            </li>
                          )}
                          {plan.features?.live_web_research && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Live Web-Recherche
                            </li>
                          )}
                          {plan.features?.actual_value_assessment && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Istwert-Beurteilung
                            </li>
                          )}
                        </>
                      )}
                      
                      {/* FMS Plan Features */}
                      {activePlanType === 'fms' && (
                        <>
                          {plan.limits?.maxVehicles && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Bis zu {plan.limits.maxVehicles} Fahrzeuge
                            </li>
                          )}
                          {plan.limits?.maxDrivers && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Bis zu {plan.limits.maxDrivers} Fahrer
                            </li>
                          )}
                          {plan.features?.live_gps_tracking && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Live GPS-Tracking
                            </li>
                          )}
                          {plan.features?.obd_remote_diagnostics && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              OBD-Ferndiagnose
                            </li>
                          )}
                          {plan.features?.vehicle_booking_system && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Fahrzeugpool-Buchung
                            </li>
                          )}
                          {plan.features?.maintenance_scheduling && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Wartungsplanung
                            </li>
                          )}
                          {plan.features?.driver_efficiency_scoring && (
                            <li className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                              <Check className="w-5 h-5" style={{ color: '#22C55E' }} />
                              Fahrer-Effizienz-Score
                            </li>
                          )}
                        </>
                      )}
                    </ul>
                    {selectedPlan?.id === plan.id && (
                      <div
                        className="p-2 rounded text-center font-semibold"
                        style={{ backgroundColor: '#1EA0E620', color: '#1EA0E6' }}
                      >
                        ✓ Ausgewählt
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* User Data Form */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#E6EDF3' }}>
              2. Ihre Daten
            </h2>
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Account Info */}
                  <div>
                    <h3 className="font-semibold mb-4" style={{ color: '#7CD4FD' }}>Account-Informationen</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>E-Mail *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="ihre@email.de"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="Max Mustermann"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Passwort *</Label>
                        <Input
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          placeholder="Min. 6 Zeichen"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Passwort bestätigen *</Label>
                        <Input
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          placeholder="Passwort wiederholen"
                          style={{ 
                            backgroundColor: '#0B0F14', 
                            borderColor: formData.confirmPassword && formData.password === formData.confirmPassword ? '#22C55E' : '#1F2937', 
                            color: '#E6EDF3' 
                          }}
                        />
                        {formData.confirmPassword && (
                          <p className="text-xs mt-1" style={{ 
                            color: formData.password === formData.confirmPassword ? '#22C55E' : '#EF4444' 
                          }}>
                            {formData.password === formData.confirmPassword ? '✓ Passwörter stimmen überein' : '✗ Passwörter stimmen nicht überein'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Personal/Company Info */}
                  <div>
                    <h3 className="font-semibold mb-4" style={{ color: '#7CD4FD' }}>Persönliche/Firmen-Daten</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Firma/Werkstatt</Label>
                        <Input
                          value={formData.company}
                          onChange={(e) => handleInputChange('company', e.target.value)}
                          placeholder="Autowerkstatt GmbH"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Telefon</Label>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="+49 123 456789"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label style={{ color: '#E6EDF3' }}>Straße & Hausnummer</Label>
                        <Input
                          value={formData.street}
                          onChange={(e) => handleInputChange('street', e.target.value)}
                          placeholder="Musterstraße 123"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>PLZ</Label>
                        <Input
                          value={formData.postal_code}
                          onChange={(e) => handleInputChange('postal_code', e.target.value)}
                          placeholder="12345"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Ort</Label>
                        <Input
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          placeholder="Musterstadt"
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label style={{ color: '#E6EDF3' }}>Land</Label>
                        <Input
                          value={formData.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#E6EDF3' }}>
              3. Zusammenfassung
            </h2>
            
            {/* Promo Active Banner - BIG & OBVIOUS */}
            {promoDetails && promoDetails.is_active && (
              <div 
                className="mb-6 p-6 rounded-xl border-2 animate-pulse"
                style={{ 
                  background: 'linear-gradient(135deg, #22C55E20 0%, #10B98120 100%)',
                  borderColor: '#22C55E',
                  boxShadow: '0 0 30px rgba(34, 197, 94, 0.3)'
                }}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="p-3 rounded-full"
                    style={{ backgroundColor: '#22C55E' }}
                  >
                    <svg className="w-6 h-6" style={{ color: '#fff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#22C55E' }}>
                      ✅ Aktion aktiv!
                    </h3>
                    <p className="text-base font-semibold mb-2" style={{ color: '#E6EDF3' }}>
                      {promoDetails.name}
                    </p>
                    <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                      {promoDetails.description}
                    </p>
                    <div className="space-y-2">
                      <div 
                        className="flex items-center gap-2 p-2 rounded"
                        style={{ backgroundColor: '#22C55E15' }}
                      >
                        <span className="text-2xl">🎁</span>
                        <span className="font-bold" style={{ color: '#E6EDF3' }}>
                          {promoDetails.discount_type === 'percentage' 
                            ? `${promoDetails.discount_value}% Rabatt` 
                            : `€${promoDetails.discount_value} Rabatt`}
                        </span>
                      </div>
                      {promoDetails.first_month_free && (
                        <div 
                          className="flex items-center gap-2 p-2 rounded"
                          style={{ backgroundColor: '#7CD4FD15' }}
                        >
                          <span className="text-2xl">🆓</span>
                          <span className="font-bold" style={{ color: '#7CD4FD' }}>
                            Erster Monat gratis
                          </span>
                        </div>
                      )}
                      {promoDetails.free_item && (
                        <div 
                          className="flex items-center gap-2 p-2 rounded"
                          style={{ backgroundColor: '#F59E0B15' }}
                        >
                          <span className="text-2xl">📦</span>
                          <span className="font-bold" style={{ color: '#F59E0B' }}>
                            {promoDetails.free_item} gratis (€{promoDetails.free_item_value})
                          </span>
                        </div>
                      )}
                    </div>
                    <div 
                      className="mt-4 p-3 rounded-lg"
                      style={{ backgroundColor: '#22C55E', color: '#0B0F14' }}
                    >
                      <p className="text-center font-bold">
                        💯 Diese Vorteile sind garantiert in Ihrer Bestellung enthalten!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
              <CardHeader>
                <CardTitle style={{ color: '#E6EDF3' }}>Bestellübersicht</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPlan ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm" style={{ color: '#94A3B8' }}>Gewählter Plan</p>
                      <p className="text-xl font-bold" style={{ color: '#E6EDF3' }}>{selectedPlan.name}</p>
                    </div>
                    
                    {/* Dongle Upsell Section - COMING SOON - Hidden for now */}
                    {/* Dongle feature will be enabled after launch */}
                    
                    <div className="border-t pt-4" style={{ borderColor: '#1F2937' }}>
                      <div className="flex justify-between mb-2">
                        <span style={{ color: '#94A3B8' }}>Plan-Preis</span>
                        <span style={{ color: '#E6EDF3' }}>{formatPrice(selectedPlan.price)}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span style={{ color: '#94A3B8' }}>Intervall</span>
                        <span style={{ color: '#E6EDF3' }}>
                          {(selectedPlan.billing_interval === 'monthly' || selectedPlan.interval === 'month') ? 'Monatlich' : 
                           (selectedPlan.billing_interval === 'yearly' || selectedPlan.interval === 'year') ? 'Jährlich' : 'Einmalig'}
                        </span>
                      </div>
                      {applyCredits && availableCredits > 0 && (
                        <div 
                          className="my-2 p-3 rounded-lg border-2"
                          style={{ 
                            backgroundColor: '#22C55E10',
                            borderColor: '#22C55E'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">💰</span>
                            <span className="font-bold" style={{ color: '#22C55E' }}>
                              CREDITS ANGEWENDET
                            </span>
                          </div>
                          <div className="flex justify-between font-bold" style={{ color: '#22C55E' }}>
                            <span>Rabatt durch Credits:</span>
                            <span>-{formatPrice(Math.min(availableCredits, selectedPlan.price))}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Early Bird Discount Display */}
                      {earlyBirdInfo && earlyBirdInfo.current_tier && !promoDetails && (
                        <div 
                          className="my-3 p-4 rounded-lg border-2 relative overflow-hidden"
                          style={{ 
                            backgroundColor: '#F59E0B10',
                            borderColor: '#F59E0B'
                          }}
                        >
                          {/* Animated gradient background */}
                          <div 
                            className="absolute inset-0 opacity-20"
                            style={{
                              background: 'linear-gradient(45deg, #F59E0B 0%, #FCD34D 50%, #F59E0B 100%)',
                              backgroundSize: '200% 200%',
                              animation: 'gradient 3s ease infinite'
                            }}
                          />
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{earlyBirdInfo.current_tier.badge || '🐦'}</span>
                                <div>
                                  <span className="font-bold text-lg" style={{ color: '#F59E0B' }}>
                                    {earlyBirdInfo.current_tier.name}
                                  </span>
                                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold" 
                                    style={{ backgroundColor: '#F59E0B', color: '#0B0F14' }}>
                                    LEBENSLANG
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-black" style={{ color: '#F59E0B' }}>
                                  -{earlyBirdInfo.current_tier.discount_percent}%
                                </p>
                              </div>
                            </div>
                            <div className="flex justify-between text-sm mb-1" style={{ color: '#94A3B8' }}>
                              <span>Originalpreis:</span>
                              <span className="line-through">{formatPrice(selectedPlan.price)}</span>
                            </div>
                            <div className="flex justify-between font-bold mb-2" style={{ color: '#F59E0B' }}>
                              <span>Early Bird Rabatt:</span>
                              <span>-{formatPrice(selectedPlan.price - calculateDiscountedPrice(selectedPlan.price))}</span>
                            </div>
                            <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: '#F59E0B30' }}>
                              <span className="font-bold" style={{ color: '#22C55E' }}>
                                {formatPrice(calculateDiscountedPrice(selectedPlan.price))}/Monat
                              </span>
                            </div>
                            
                            {/* Payment & Access Info */}
                            <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#0B0F14' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">💳</span>
                                <span className="font-bold text-sm" style={{ color: '#22C55E' }}>
                                  SOFORTIGE ZAHLUNG
                                </span>
                              </div>
                              <div className="text-xs space-y-1" style={{ color: '#94A3B8' }}>
                                <div className="flex items-center gap-2">
                                  <span>✅</span>
                                  <span>Betrag wird <strong style={{ color: '#E6EDF3' }}>heute</strong> abgebucht</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span>🚀</span>
                                  <span>Zugang ab <strong style={{ color: '#FCD34D' }}>31. Januar 2026</strong> (Launch)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span>🔄</span>
                                  <span>Nächste Abbuchung: <strong style={{ color: '#E6EDF3' }}>März 2026</strong></span>
                                </div>
                              </div>
                            </div>
                            
                            <p className="text-xs mt-2 text-center" style={{ color: '#FCD34D' }}>
                              🔒 Ihr {earlyBirdInfo.current_tier.discount_percent}% Rabatt bleibt LEBENSLANG auf Ihrem Konto!
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {promoDetails && promoDetails.is_active && (
                        <div 
                          className="my-3 p-3 rounded-lg border-2"
                          style={{ 
                            backgroundColor: '#22C55E10',
                            borderColor: '#22C55E'
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xl">✅</span>
                            <span className="font-bold" style={{ color: '#22C55E' }}>
                              AKTION AKTIV
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mb-1" style={{ color: '#94A3B8' }}>
                            <span>Originalpreis:</span>
                            <span className="line-through">{formatPrice(selectedPlan.price)}</span>
                          </div>
                          <div className="flex justify-between font-bold mb-1" style={{ color: '#22C55E' }}>
                            <span>Rabatt ({promoCode}):</span>
                            <span>-{formatPrice(selectedPlan.price - calculateDiscountedPrice(selectedPlan.price))}</span>
                          </div>
                          {promoDetails.first_month_free && selectedPlan.interval === 'month' && (
                            <div className="flex justify-between font-bold" style={{ color: '#7CD4FD' }}>
                              <span>🎁 Erster Monat:</span>
                              <span>GRATIS</span>
                            </div>
                          )}
                          {promoDetails.free_item && (
                            <div className="flex justify-between font-bold mt-1" style={{ color: '#F59E0B' }}>
                              <span>📦 Gratis:</span>
                              <span>{promoDetails.free_item}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t" style={{ borderColor: '#1F2937' }}>
                        <span style={{ color: '#E6EDF3' }}>Gesamt (Heute)</span>
                        <span style={{ color: (promoDetails || earlyBirdInfo || applyCredits || addDongle) ? '#22C55E' : '#7CD4FD' }}>
                          {formatPrice((() => {
                            // Apply early bird or promo discount
                            let finalPrice = calculateDiscountedPrice(selectedPlan.price);
                            if (applyCredits && availableCredits > 0) {
                              finalPrice = Math.max(0, finalPrice - availableCredits);
                            }
                            // Add dongle price if selected and not free
                            if (addDongle && selectedDongle && !dongleFreeWithPromo) {
                              finalPrice += selectedDongle.price;
                            }
                            return finalPrice;
                          })())}
                        </span>
                      </div>
                      {addDongle && selectedDongle && selectedPlan.interval !== 'lifetime' && (
                        <div className="mt-2 p-2 rounded bg-[#7CD4FD]/10 border border-[#7CD4FD]/30">
                          <p className="text-xs text-[#7CD4FD] flex items-center gap-1">
                            <CreditCard className="w-3 h-3" />
                            <span>Ab nächstem Monat: {formatPrice(selectedPlan.price)}/Monat (nur Plan)</span>
                          </p>
                        </div>
                      )}
                      {applyCredits && availableCredits >= selectedPlan.price && (
                        <p className="text-sm text-center font-bold mt-2" style={{ color: '#22C55E' }}>
                          ✅ Vollständig durch Credits abgedeckt!
                        </p>
                      )}
                    </div>
                    {/* Promo Code */}
                    {promoCode ? (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: '#22C55E20', border: '1px solid #22C55E' }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: '#22C55E' }}>✓ Gutschein aktiv:</span>
                            <span className="font-mono font-bold" style={{ color: '#E6EDF3' }}>{promoCode}</span>
                          </div>
                          <button
                            onClick={() => {
                              setPromoCode('');
                              setPromoDetails(null);
                              sessionStorage.removeItem('promo_code');
                              sessionStorage.removeItem('promo_details');
                              toast.info('Gutschein entfernt');
                            }}
                            className="text-xs underline"
                            style={{ color: '#94A3B8' }}
                          >
                            Entfernen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Label style={{ color: '#E6EDF3' }}>Gutscheincode (optional)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="z.B. XMAS2024"
                            style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                          />
                          <Button
                            onClick={async () => {
                              if (!promoCode) return;
                              try {
                                const response = await axios.get(`${API}/admin/aktionen`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                const matchingPromo = response.data.find(p => p.coupon_code === promoCode && p.is_active);
                                if (matchingPromo) {
                                  setPromoDetails(matchingPromo);
                                  toast.success('Gutschein erfolgreich angewendet!');
                                } else {
                                  toast.error('Ungültiger oder inaktiver Gutscheincode');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Überprüfen des Gutscheins');
                              }
                            }}
                            style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                          >
                            Anwenden
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Credits Application - Only show if user has credits */}
                    {availableCredits > 0 && (
                      <div className="p-4 rounded-lg border-2" style={{ 
                        backgroundColor: '#22C55E10',
                        borderColor: '#22C55E'
                      }}>
                        <div className="flex items-start gap-2 mb-3">
                          <input
                            type="checkbox"
                            id="apply-credits"
                            checked={applyCredits}
                            onChange={(e) => setApplyCredits(e.target.checked)}
                            className="mt-1 w-5 h-5 rounded"
                            style={{ accentColor: '#22C55E' }}
                          />
                          <div className="flex-1">
                            <label htmlFor="apply-credits" className="font-bold text-base cursor-pointer" style={{ color: '#22C55E' }}>
                              💰 Credits anwenden
                            </label>
                            <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                              Sie haben <strong style={{ color: '#22C55E' }}>€{availableCredits.toFixed(2)}</strong> verfügbare Credits
                            </p>
                          </div>
                        </div>
                        {applyCredits && (
                          <div className="mt-3 p-3 rounded" style={{ backgroundColor: '#22C55E20' }}>
                            <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                              ✅ Credits werden beim Checkout angewendet
                            </p>
                            <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                              {availableCredits >= (selectedPlan?.price || 0) 
                                ? `Ihre Credits decken den gesamten Betrag ab!`
                                : `€${availableCredits.toFixed(2)} werden von €${selectedPlan?.price} abgezogen`}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Newsletter Checkbox */}
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <input
                        type="checkbox"
                        id="newsletter-signup"
                        checked={formData.subscribeNewsletter}
                        onChange={(e) => handleInputChange('subscribeNewsletter', e.target.checked)}
                        className="mt-1 w-4 h-4 rounded"
                        style={{ accentColor: '#1EA0E6' }}
                      />
                      <label htmlFor="newsletter-signup" className="text-sm" style={{ color: '#E6EDF3' }}>
                        📧 Newsletter abonnieren (empfohlen) - Erhalten Sie exklusive Tipps, Updates und Angebote
                      </label>
                    </div>

                    {/* Final Confirmation before Checkout */}
                    {promoDetails && promoDetails.is_active && (
                      <div 
                        className="p-4 rounded-lg mb-4"
                        style={{ 
                          background: 'linear-gradient(135deg, #22C55E 0%, #10B981 100%)',
                          color: '#fff'
                        }}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="font-bold text-lg">✅ Aktion wird angewendet!</p>
                            <p className="text-sm opacity-90">Ihre Vorteile sind gesichert</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleCheckout}
                      disabled={processing}
                      className="w-full"
                      style={{ 
                        backgroundColor: promoDetails ? '#22C55E' : '#1EA0E6', 
                        color: '#ffffff',
                        fontSize: '1.1rem',
                        padding: '1.5rem'
                      }}
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      {processing ? 'Weiterleitung...' : promoDetails ? '🎁 Mit Aktion fortfahren' : 'Jetzt bestellen'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    
                    {promoDetails && promoDetails.is_active && (
                      <p className="text-sm text-center font-bold mt-2" style={{ color: '#22C55E' }}>
                        ✓ {promoCode} wird bei Stripe automatisch angewendet
                      </p>
                    )}
                    
                    <p className="text-xs text-center" style={{ color: '#94A3B8' }}>
                      Sichere Zahlung über Stripe
                    </p>
                  </div>
                ) : (
                  <p style={{ color: '#94A3B8' }}>Bitte wählen Sie einen Plan aus</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center mt-8">
          <p style={{ color: '#94A3B8' }}>
            Bereits registriert?{' '}
            <button
              onClick={() => navigate('/auth')}
              className="font-semibold hover:underline"
              style={{ color: '#1EA0E6' }}
            >
              Zum Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
